'use client'

import { useSharedProductApisReady } from '@/app/[locale]/_hooks/useSharedProductApisReady'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import productService from '@/app/[locale]/_service/product.service'
import { IRedux } from '@/app/[locale]/_store/types'
import {
  resolvePlatformId,
  resolveSourceId,
} from '@/app/[locale]/_utils/aggregator-context'
import {
  getMiniAppCrmType,
  getMiniAppProvider,
} from '@/app/[locale]/_utils/mini-app'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { IProduct } from '../../_types'

const PRODUCT_DETAIL_STALE_TIME = 5 * 60 * 1000

export function useProductModalData(preview: IProduct) {
  const apisReady = useSharedProductApisReady()
  const { useDynamicLanguage: dynamicLanguageEnabled, isShipperResolved } =
    useDynamicLanguage()
  const { user, branch, session, shipper_id, platform_id, source_id } =
    useSelector((state: IRedux) => {
      const resolvedSourceId = resolveSourceId(state.session, state.auth)
      return {
        user: state.auth.user,
        branch: state.common.branch,
        session: state.session,
        shipper_id: state.settings?.data?.shipper_id,
        platform_id: resolvePlatformId(state.session, resolvedSourceId),
        source_id: resolvedSourceId,
      }
    }, shallowEqual)

  const { is_hall, menu_id, order_type } = session
  const productId = preview.id

  const selectedMenuId =
    is_hall === 'true'
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || ''

  const { data: fullProduct } = useQuery({
    queryKey: [
      'product-by-id',
      productId,
      selectedMenuId,
      shipper_id,
      order_type?.type,
      is_hall,
      dynamicLanguageEnabled,
    ],
    queryFn: () => {
      const params = {
        only_delivery: String(order_type?.type === 'delivery'),
        only_self_pickup: String(order_type?.type === 'self-pickup'),
        branch_id: branch?.id || '',
        menu_id: selectedMenuId,
        client_id: user?.id || '',
        with_discounts: 'true',
        aggregator_id: source_id,
        order_source: getMiniAppCrmType(getMiniAppProvider(null)),
        order_type_id: order_type?.id,
      }

      return productService
        .getById(
          productId,
          params,
          shipper_id,
          platform_id,
          dynamicLanguageEnabled
        )
        .then((res) => res?.data)
    },
    enabled: Boolean(productId) && apisReady && Boolean(shipper_id) && isShipperResolved,
    staleTime: PRODUCT_DETAIL_STALE_TIME,
  })

  return useMemo(
    () => (fullProduct ? { ...preview, ...fullProduct } : preview),
    [preview, fullProduct]
  )
}
